import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Activity } from '@app/_components/activity';
import { retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';
import { ACTIVITY_CATEGORIES, ACTIVITY_TYPES } from './activity-constants';
import { PropertyService } from '@app/_components/property/property.service';

@Injectable({ providedIn: 'root' })
export class ActivityService {

	statuses: any = [
		{ value: 'active',   name: 'Active' },
		{ value: 'draft',    name: 'Draft' },
		{ value: 'archived', name: 'Archived' },
	];

	categories: any = ACTIVITY_CATEGORIES;

	/** Populated lazily from the properties API; empty until loadProperties() resolves. */
	properties: string[] = [];
	propertiesLoaded = false;

	leadTimes: any = [
		{ value: 'No minimum', name: 'No minimum' },
		{ value: '24 hours',   name: '24 hours' },
		{ value: '48 hours',   name: '48 hours' },
		{ value: '72 hours',   name: '72 hours' },
		{ value: '1 week',     name: '1 week' },
	];

	seasonalOptions: any = [
		{ value: 'Year-round',            name: 'Year-round' },
		{ value: 'Summer only (May–Sep)', name: 'Summer only (May–Sep)' },
		{ value: 'Winter only (Oct–Apr)', name: 'Winter only (Oct–Apr)' },
		{ value: 'Custom date range',     name: 'Custom date range' },
	];

	cancelPolicies: any = [
		{ value: 'Use provider default',   name: 'Use provider default' },
		{ value: '24-hour cancellation',   name: '24-hour cancellation' },
		{ value: '48-hour cancellation',   name: '48-hour cancellation' },
		{ value: '72-hour cancellation',   name: '72-hour cancellation' },
		{ value: 'Non-refundable',         name: 'Non-refundable' },
	];

	timeSlots: any = [
		'6:00 AM','7:00 AM','8:00 AM','9:00 AM','10:00 AM','11:00 AM',
		'12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM','7:00 PM',
	];

	availabilityList: any = [
		{ value: 'Mon', name: 'Mon' },
		{ value: 'Tue', name: 'Tue' },
		{ value: 'Wed', name: 'Wed' },
		{ value: 'Thu', name: 'Thu' },
		{ value: 'Fri', name: 'Fri' },
		{ value: 'Sat', name: 'Sat' },
		{ value: 'Sun', name: 'Sun' },
		{ value: 'Daily', name: 'Daily' },
	];

	pickUpList: any = [
		{ value: 'Pick-up not available', name: 'Pick-up not available' },
		{ value: 'pick-up available', name: 'pick-up available' },
	];

	accessibleList: any = [
		{ value: 'Accessible', name: 'Accessible' },
		{ value: 'Not Accessible', name: 'Not Accessible' },
	];

	eatingList: any = [
		{ value: 'Lunch', name: 'Lunch' },
		{ value: 'Dinner', name: 'Dinner' },
		{ value: 'Lunch, Dinner', name: 'Lunch, Dinner' },
	];

	languageList: any = [
		{ value: 'English', name: 'English' },
	];
	
	activityTypes: any = ACTIVITY_TYPES;

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService,
			private propertyService: PropertyService,
		) { }

	urlEndpoint = 'activitys';

	/** Lazy-load property names from the API. Safe to call multiple times — only fetches once. */
	loadProperties(): void {
		if (this.propertiesLoaded) return;
		this.propertyService.propertySummaryForShortData().subscribe(
			(res: any) => {
				const raw: any[] = res?.data?.properties || [];
				this.properties = raw
					.map((p: any) => p.propertyCode || p.name || '')
					.filter(Boolean)
					.sort();
				this.propertiesLoaded = true;
			},
			(err: any) => {
				console.error('ActivityService.loadProperties failed:', err);
				this.properties = [];
			}
		);
	}

	add(activity: Activity): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(activity));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, activity);
	}

	update(activity: Activity): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, activity);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(size: number = 200): Observable<ApiResponse> {
		const params = new HttpParams().set('size', String(size));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	aiPopulate(activityId: number, providerUrl: string): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${activityId}/ai-populate`, { providerUrl });
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
